import * as React from "react";
import {useRef} from "react";
import {Outlet, useNavigate, useParams} from "react-router-dom";
import Page from "../../layout/Page";
import PageHeader from "../../layout/PageHeader";
import PageContent from "../../layout/PageContent";
import {BrandItemsList} from "./BrandItemsList";
import {
   BrandItemStateEnum,
   EnumIntegrationConnectorMessages,
   FlexBrandItemFragment,
   useBrandItemDeleteMutation,
   useBrandItemImageAutoAdjustMutation,
   useBrandItemRetouchMutation,
   useBrandItemsPageChangeSortOrderMutation,
   useBrandItemsPageChangeStateMutation,
   useBrandItemsPageShopsQuery,
   useBrandItemsUpdateCacheMutation,
   useBrandItemUpdateGroupMutation,
   useBrandItemUpdateIntegrationMutation,
   useBrandItemUpdatePricesMutation,
   useOptimizeBrandItemsBySalesRankMutation,
   useBrandItemCreateFromMasterDataMutation,
   useBrandItemsBackfillMasterDataMutation,
   useBrandItemsPreviewMasMigrationMutation,
   useBrandItemsRefreshMasterDataMutation
} from "../../Queries";
import {MasMigrationRunPagePath} from "../admin/masmigration/MasMigrationRunPage";
import {BrandItemCreatePagePath, BrandItemPagePath} from "./brandItem/BrandItemPage";
import {Box, Button, Drawer, LinearProgress} from "@mui/material";
import {MasterDataSearchDialog} from "./MasterDataSearchDialog";
import {ProformaOrderDialog} from "./ProformaOrderDialog";
import {RangeCommand} from "../../components/rangelist/RangeListTypes";
import {Bolt, CompareArrows, ContentPaste, EditAttributes, EditAttributesOutlined, FileDownload, ImageAspectRatioOutlined, SettingsRemote, Storefront, Sync} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {SavingUI} from "../../SavingProvider";
import {Dialogs} from "../../DialogProvider";
import EditIcon from "@mui/icons-material/Edit";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin, UserSimplified} from "../../atoms/CurrentUser";
import {ImportItems, ImportItemsFromExcel, ImportRowType} from "./BrandItemImport";
import {useApolloClient} from "@apollo/client";
import {TitleText} from "../../layout/Typography";
import {BrandItemExport} from "./BrandItemExport";
import {ParamBrandHouse, ParamBrandItem} from "../../Parameters";
import useLocalStorage from "../../hooks/useLocalStorage";
import CampaignPricing from "./CampaignPricing";
import {BrandItemBoostDialog} from "./BrandItemBoostDialog";
import {BrandItemPublishDialog} from "./BrandItemPublishDialog";
import {useRangeListUpdater} from "../../components/rangelist/RangeListTypes";
import {ViewFilter} from "../../components/ViewFilter";
import {uniq} from "lodash";

export const BrandItemsPagePath = (brandHouseId: string) => {
   return "/brands/" + brandHouseId + "/stock";
}

export const BrandItemsPageWrapper = () => {
   const {brandHouseId, brandItemId} = useParams<ParamBrandHouse & ParamBrandItem>();
   const navigate = useNavigate();

   const onCloseDrawer = () => {
      navigate(BrandItemsPagePath(brandHouseId))
   }

   const drawerOpen = !!brandItemId
   return (
      <>
         <BrandItemsPage/>
         {brandItemId && (
            <Drawer anchor="right" open={drawerOpen} onClose={onCloseDrawer}
                    PaperProps={{style: {width: '70%', backgroundColor: '#f8f8f8'}}}>
               <Box sx={{padding: '20px'}}>
                  <Outlet/>
               </Box>
            </Drawer>
         )}
      </>
   );

}

export const BrandItemsPage = () => {
   const {brandHouseId} = useParams<{ brandHouseId: string }>();
   const navigate = useNavigate();
   const [editCampaign, setEditCampaign] = React.useState<boolean>(false);
   const [exportIds, setExportIds] = React.useState<string[]>([])
   const [boostIds, setBoostIds] = React.useState<string[]>([])
   const [publishIds, setPublishIds] = React.useState<string[]>([])
   // Boosts and publishing change what the rows show, so the affected rows are reloaded
   // rather than left showing what the list last fetched.
   const {reloadRows} = useRangeListUpdater("brandStockItemList");
   const [importRows, setImportRows] = React.useState<ImportRowType[]>([]);
   const importSearchField = useRef<string>('');
   const [creating, setCreating] = React.useState<boolean>(false);
   const [proformaDialogIds, setProformaDialogIds] = React.useState<string[]>([]);
   const {data} = useBrandItemsPageShopsQuery({
      variables: {
         brandHouseId
      }
   });
   const client = useApolloClient();
   const [changeState] = useBrandItemsPageChangeStateMutation()
   const [deleteBrandItem] = useBrandItemDeleteMutation()
   const [retouchImages] = useBrandItemRetouchMutation()
   const [updateCache] = useBrandItemsUpdateCacheMutation()
   const [updateSortOrder] = useBrandItemsPageChangeSortOrderMutation()
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);
   const isSimplified = useRecoilValue(UserSimplified);
   const importRef = useRef<HTMLInputElement | null>(null)
   const [useDrawer] = useLocalStorage<boolean>('c2s-stockitem-drawer', true);
   const [adjustImages] = useBrandItemImageAutoAdjustMutation()
   const [updateGroup] = useBrandItemUpdateGroupMutation();
   const [updatePrices] = useBrandItemUpdatePricesMutation();
   const [updateIntegration] = useBrandItemUpdateIntegrationMutation();
   const [createFromMasterData] = useBrandItemCreateFromMasterDataMutation();
   const [optimizeBySalesRank] = useOptimizeBrandItemsBySalesRankMutation();
   const [previewMasMigration] = useBrandItemsPreviewMasMigrationMutation();
   const [backfillMasterData] = useBrandItemsBackfillMasterDataMutation();
   const [refreshMasterData] = useBrandItemsRefreshMasterDataMutation();

   const onSelectBrandItem = (brandItem: FlexBrandItemFragment, newWindow?: boolean) => {
      if (newWindow) {
         window.open(BrandItemPagePath(brandHouseId, brandItem._id, false), '_blank', '')
      } else {
         navigate(BrandItemPagePath(brandHouseId, brandItem._id, useDrawer))
      }
   }
   const onCreating = () => {
      setCreating(true);
   }
   const onCancel = () => {
      setCreating(false);
   }
   const onCreateFromMasterData = async (masterDataVariantId: string) => {
      const {data: created} = await createFromMasterData({
         variables: {brandHouseId, masterDataVariantId}
      })
      setCreating(false)
      const newId = created?.BrandItemUpdate?._id
      if (newId) {
         navigate(BrandItemPagePath(brandHouseId, newId, false))
      }
   }

   const onCreateEmpty = () => {
      navigate(BrandItemCreatePagePath(brandHouseId))
   }

   const onOptimizeBySalesRank = async () => {
      Dialogs.confirm({
         title: 'Optimize by Sales Rank?',
         subtitle: 'This will optimize the order of items for sale in the storefront (if supported).'
      }, async () => {
         await SavingUI.process(async () => {
            await optimizeBySalesRank({
               variables: {
                  brandHouseId: brandHouseId
               }
            })
         }, 'Optimization complete', 'Optimizing, please wait')
      })
   }

   if (!data) {
      return <LinearProgress/>
   }

   const onSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target?.files?.[0];
      if (file) {
         const importResult = await ImportItemsFromExcel(file);
         Dialogs.confirm({
            title: 'Import ' + importResult.name + '?',
            subtitle: 'File contains ' + importResult.rows.length + ' lines. Search using ' + importResult.search
         }, () => {
            importSearchField.current = importResult.search
            setImportRows(importResult.rows);
         })
      }
   }
   const onStartImport = () => {
      importRef.current?.click()
   }

   const onImportDone = async () => {
      setImportRows([])
      await client.refetchQueries({
         include: ['BrandItemsListMany']
      });
   }

   const onChangeState = async (ids: string[], state: BrandItemStateEnum) => {
      await SavingUI.process(async () => {
         for (const id of ids) {
            await changeState({
               variables: {
                  brandHouseId: brandHouseId,
                  id: id,
                  state: state
               }
            })
         }
      }, 'Done', 'Changing state, please wait')
   }

   const shops = (data?.ShopMany || []).map(shop => ({
      _id: shop._id,
      name: shop.name || 'Shop'
   }));

   const commands: RangeCommand<FlexBrandItemFragment>[] = [{
      id: 'publishing',
      title: 'Edit publishing',
      icon: Storefront,
      execute: async (ids: string[]) => {
         setPublishIds(ids)
      }
   }, {
      id: 'boost',
      title: 'Boost',
      icon: Bolt,
      execute: async (ids: string[]) => {
         setBoostIds(ids)
      }
   }, {
      id: 'export',
      title: 'Export to excel',
      icon: FileDownload,
      execute: async (ids: string[]) => {
         setExportIds(ids)
      }
   }]

   if (isAdministrator) {
      commands.push({
         id: 'changeState',
         title: 'Change state',
         icon: EditAttributesOutlined,
         children: Object.keys(BrandItemStateEnum).map(key => ({
            execute: async (ids: string[]) => onChangeState(ids, key as BrandItemStateEnum),
            icon: EditAttributes,
            title: key,
            id: key
         }))
      })
      const advancedCommand: RangeCommand<FlexBrandItemFragment> = {
         id: 'imageoperations',
         title: 'Advanced',
         icon: EditAttributesOutlined,
         children: [
            {
               id: 'retouch',
               title: 'Retouch images',
               icon: ImageAspectRatioOutlined,
               execute: async (ids: string[]) => {
                  Dialogs.confirm({
                     title: 'Start retouch of images from selected products?',
                     subtitle: 'You will not be able to undo this operation.'
                  }, async () => {
                     await SavingUI.process(async () => {
                        await retouchImages({
                           variables: {
                              ids: ids
                           }
                        })
                     }, 'Retouch started', 'Initializing retouch, please wait')
                  })
               }
            },
            {
               id: 'proforma',
               title: 'Create proforma order',
               icon: ContentPaste,
               execute: async (ids: string[]) => {
                  const shop = data.ShopMany?.find(shop => shop?.live) || data.ShopMany?.[0];
                  if (!shop) {
                     Dialogs.info({
                        title: 'No live shop',
                        subtitle: 'No live shop found, cannot create proforma invoice'
                     });
                     return;
                  }
                  setProformaDialogIds(ids);
               }
            },
            {
               id: 'adjustImages',
               title: 'Auto adjust images',
               icon: EditIcon,
               execute: async (ids: string[]) => {
                  await SavingUI.process(async () => {
                     for (const id of ids) {
                        await adjustImages({
                           variables: {
                              brandItemId: id
                           }
                        })
                     }
                  }, 'Adjust complete', 'Adjusting master images, please wait')

               },
            },
            {
               id: 'groupUpdate',
               title: 'Update masterdata group',
               icon: DeleteIcon,
               execute: async (ids: string[]) => {
                  Dialogs.confirm({
                     title: 'Are you sure you want to update masterdata for these items?',
                     subtitle: 'You will not be able to undo this operation.'
                  }, async () => {
                     await SavingUI.process(async () => {
                        const {data} = await updateGroup({
                           variables: {
                              brandItemIds: ids
                           }
                        })
                        if ((data?.BrandItemGroupUpdate || []).length > 0) {
                           Dialogs.info({
                              title: 'Group update',
                              subtitle: 'Updated ' + (data?.BrandItemGroupUpdate?.length || 0) + ' items'
                           })
                        }
                     }, 'Done', 'Updating, please wait')
                  })
               }
            },
            {
               id: 'updateCache',
               title: 'Synchronize selected items',
               icon: ImageAspectRatioOutlined,
               execute: async (ids: string[]) => {
                  Dialogs.confirm({
                     title: 'Start a synchronization of selected items?',
                     subtitle: 'This will update external integrations data for the selected items.'
                  }, async () => {
                     await SavingUI.process(async () => {
                        for (const id of ids) {
                           await updateCache({
                              variables: {
                                 brandItemId: id
                              }
                           })
                        }
                     }, 'Done', 'Please wait')
                  })
               }
            },
            {
               id: 'updatePrices',
               title: 'Update prices',
               icon: EditIcon,
               execute: async (ids: string[]) => {
                  Dialogs.confirm({
                     title: 'Update prices for selected items?',
                     subtitle: 'This will update the prices for the selected brand items.'
                  }, async () => {
                     await SavingUI.process(async () => {
                        const {data} = await updatePrices({
                           variables: {
                              brandItemIds: ids
                           }
                        })
                        if ((data?.BrandItemUpdatePrices || []).length > 0) {
                           Dialogs.info({
                              title: 'Prices updated',
                              subtitle: 'Updated prices for ' + (data?.BrandItemUpdatePrices?.length || 0) + ' items'
                           })
                        }
                     }, 'Done', 'Updating prices, please wait')
                  })
               }
            },
            {
               id: 'updateSortorder',
               title: 'Update sortorder',
               icon: EditIcon,
               execute: async (ids: string[]) => {
                  const sortOrder = prompt('Enter sortorder');
                  if (!sortOrder) {
                     return;
                  }
                  const sortOrderInt = parseInt(sortOrder);

                  Dialogs.confirm({
                     title: 'Update sortorder?',
                     subtitle: 'This will update the sortorder for selected items to ' + sortOrderInt + '.'
                  }, async () => {
                     await SavingUI.process(async () => {
                        for (const id of ids) {
                           await updateSortOrder({
                              variables: {
                                 id: id,
                                 brandHouseId,
                                 order: sortOrderInt
                              }
                           })
                        }
                     }, 'Done', 'Updating prices, please wait')
                  })
               }
            },
            {
               id: 'previewMasMigration',
               title: 'Migrate to masterdata',
               icon: CompareArrows,
               execute: async (ids: string[]) => {
                  if (ids.length === 0) {
                     Dialogs.info({
                        title: 'No items selected',
                        subtitle: 'Select the brand items you want to migrate to MAS.'
                     });
                     return;
                  }
                  await SavingUI.process(async () => {
                     const {data} = await previewMasMigration({
                        variables: {brandItemIds: ids}
                     });
                     const runId = data?.BrandItemsPreviewMasterDataVariantMigration?._id;
                     if (runId) {
                        navigate(MasMigrationRunPagePath(runId));
                     }
                  }, 'Preview started', 'Starting MAS migration preview, please wait');
               }
            },
            {
               id: 'backfillMasterData',
               title: 'Backfill from masterdata',
               icon: EditIcon,
               execute: async (ids: string[]) => {
                  Dialogs.confirm({
                     title: 'Backfill masterdata for selected items?',
                     subtitle: 'Fills in only fields that are currently missing (e.g. prices) from masterdata. Existing values are left unchanged.'
                  }, async () => {
                     await SavingUI.process(async () => {
                        const {data} = await backfillMasterData({
                           variables: {brandItemIds: ids}
                        });
                        Dialogs.info({
                           title: 'Backfill queued',
                           subtitle: `Queued ${data?.BrandItemsBackfillMasterData?.queued ?? 0} item(s), skipped ${data?.BrandItemsBackfillMasterData?.skipped ?? 0}.`
                        });
                     }, 'Done', 'Queuing backfill, please wait')
                  })
               }
            },
            {
               id: 'refreshMasterData',
               title: 'Refresh from masterdata',
               icon: Sync,
               execute: async (ids: string[]) => {
                  Dialogs.confirm({
                     title: 'Refresh (overwrite) masterdata for selected items?',
                     subtitle: 'Overwrites fields from masterdata, replacing existing values. Use when values are outdated.'
                  }, async () => {
                     await SavingUI.process(async () => {
                        const {data} = await refreshMasterData({
                           variables: {brandItemIds: ids}
                        });
                        Dialogs.info({
                           title: 'Refresh queued',
                           subtitle: `Queued ${data?.BrandItemsRefreshMasterData?.queued ?? 0} item(s), skipped ${data?.BrandItemsRefreshMasterData?.skipped ?? 0}.`
                        });
                     }, 'Done', 'Queuing refresh, please wait')
                  })
               }
            }
         ]
      };
      for (const instance of data.IntegrationInstances || []) {
         if (!instance.connector?.messages?.includes(EnumIntegrationConnectorMessages.UPDATEBRANDITEM)) {
            continue
         }
         advancedCommand.children!.push({
            id: 'integration_' + instance._id,
            title: 'Update ' + instance.name + ' integration',
            icon: SettingsRemote,
            execute: async (ids: string[]) => {
               Dialogs.confirm({
                  title: 'Update ' + instance.name + ' for selected items?',
                  subtitle: 'This will trigger integration for selected items.'
               }, async () => {
                  await SavingUI.process(async () => {
                     await updateIntegration({
                        variables: {
                           brandItemIds: uniq(ids.map(id => String(id))),
                           instanceId: instance._id
                        }
                     })
                  }, 'Done', 'Updating .. please wait')
               })
            }
         })
      }
      commands.push(advancedCommand);
      // Deleting stock items is irreversible, so it stays out of the simplified admin view.
      if (!isSimplified) {
         commands.push({
            id: 'delete',
            title: 'Delete',
            icon: DeleteIcon,
            execute: async (ids: string[]) => {
               Dialogs.confirm({
                  title: 'Delete the selected items?',
                  subtitle: 'You will not be able to undo this operation.'
               }, async () => {
                  await SavingUI.process(async () => {
                     for (const id of ids) {
                        await deleteBrandItem({
                           variables: {
                              brandItemId: id
                           }
                        })
                     }
                  }, 'Deleted', 'Deleting, please wait')
               })
            }
         })
      }
   }

   let languages = (data?.BrandSettings?.locales?.filter(locale => locale!.activeItems && locale!.locale)
      .map(locale => locale!.locale!)) || [];

   return (
      <Page>
         <PageHeader title={"Stock Items"}>
            {isAdministrator && (
               <input ref={importRef}
                      type="file"
                      id="importInputFile"
                      style={{display: 'none'}}
                      onChange={onSelectFile}
               />
            )}
            <Button onClick={() => setEditCampaign(true)}>Campaigns</Button>
            <Button onClick={onOptimizeBySalesRank}>Optimize by Sales Rank</Button>
            {isAdministrator && <Button onClick={onStartImport}>Import</Button>}
            {data?.BrandSettings?.useBrandItems && <Button variant={"contained"} onClick={onCreating}>Create</Button>}
         </PageHeader>

         <PageContent fullHeight>
            {importRows.length > 0 && (
               <Box>
                  <TitleText type={"h2"}>Importing items, please wait</TitleText>
                  <ImportItems searchField={importSearchField.current} languages={languages} rows={importRows}
                               onComplete={onImportDone}/>
               </Box>
            )}
            {importRows.length === 0 && (
               <ViewFilter dataType="stock">
                  {(viewId) => (
                     <BrandItemsList
                        listId={"brandStockItemList"}
                        brandHouseId={brandHouseId}
                        columnOptions={{id: true}}
                        onSelectItem={onSelectBrandItem}
                        commands={commands}
                        shops={shops}
                        viewId={viewId}
                     />
                  )}
               </ViewFilter>
            )}
         </PageContent>

         {creating && (
            <MasterDataSearchDialog
               brandHouseId={brandHouseId}
               onPickVariant={onCreateFromMasterData}
               onCreateEmpty={onCreateEmpty}
               onCancel={onCancel}
            />
         )}

         {editCampaign && (
            <CampaignPricing onClose={() => setEditCampaign(false)} open={editCampaign} brandHouseId={brandHouseId}/>
         )}

         {exportIds.length > 0 && <BrandItemExport language={"da"} currency={"DKK"} ids={exportIds}/>}
         {boostIds.length > 0 && <BrandItemBoostDialog
             brandItemIds={boostIds}
             open={boostIds.length > 0}
             onClose={() => setBoostIds([])}
             onCompleted={() => reloadRows(boostIds)}
         />}
         {publishIds.length > 0 && <BrandItemPublishDialog
             brandItemIds={publishIds}
             shops={shops}
             open={publishIds.length > 0}
             onClose={() => setPublishIds([])}
             onCompleted={() => reloadRows(publishIds)}
         />}
         {proformaDialogIds.length > 0 && (
            <ProformaOrderDialog
               ids={proformaDialogIds}
               brandHouseId={brandHouseId}
               shopId={(data.ShopMany?.find(shop => shop?.live) || data.ShopMany?.[0])?._id || ''}
               shopCurrency={(data.ShopMany?.find(shop => shop?.live) || data.ShopMany?.[0])?.currency || 'DKK'}
               onCancel={() => setProformaDialogIds([])}
               onComplete={() => setProformaDialogIds([])}
            />
         )}
      </Page>
   );
}
